import React, { useState, useEffect } from 'react';
import { DeskLayout } from '../../components/DeskLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Skeleton } from '../../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { getReviews, createReview, updateReviewResponse, generateReviewDraft, getReviewInsights } from '../../lib/api';
import { useProperty } from '../../context/PropertyContext';
import { Star, Plus, PenLine, TrendingUp, TrendingDown, Lightbulb, Loader2, Check } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

export const Reviews = () => {
  const { properties, selectedPropertyId, setSelectedPropertyId, getPropertyName } = useProperty();
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [selectedReview, setSelectedReview] = useState(null);
  const [draftLoading, setDraftLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [showNewReview, setShowNewReview] = useState(false);
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [draftResponse, setDraftResponse] = useState('');
  const [newReviewData, setNewReviewData] = useState({
    propertyId: '',
    platform: 'AIRBNB',
    rating: 5,
    reviewText: '',
    reviewerName: ''
  });

  useEffect(() => {
    loadData();
  }, [selectedPropertyId]);

  useEffect(() => {
    if (selectedPropertyId !== 'all') {
      loadInsights(selectedPropertyId);
    } else {
      setInsights(null);
    }
  }, [selectedPropertyId]);

  const loadData = async () => {
    try {
      const params = {};
      if (selectedPropertyId !== 'all') {
        params.propertyId = selectedPropertyId;
      }
      const reviewsRes = await getReviews(params);
      setReviews(reviewsRes.data);
    } catch (error) {
      console.error('Failed to load reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadInsights = async (propertyId) => {
    setInsightsLoading(true);
    try {
      const res = await getReviewInsights(propertyId);
      setInsights(res.data);
    } catch (error) {
      console.error('Failed to load insights:', error);
    } finally {
      setInsightsLoading(false);
    }
  };

  const filteredReviews = reviews.filter(r => {
    const matchesStatus = filterStatus === 'all' || r.status === filterStatus;
    return matchesStatus;
  });

  const handleCreateReview = async () => {
    if (!newReviewData.propertyId || !newReviewData.reviewText) {
      toast.error('Please fill required fields');
      return;
    }
    try {
      await createReview(newReviewData);
      setShowNewReview(false);
      setNewReviewData({ propertyId: '', platform: 'AIRBNB', rating: 5, reviewText: '', reviewerName: '' });
      await loadData();
      toast.success('Review added');
    } catch (error) {
      toast.error('Failed to add review');
    }
  };

  const handleGenerateDraft = async (review) => {
    setDraftLoading(true);
    setSelectedReview(review);
    try {
      const res = await generateReviewDraft(review.propertyId, review.reviewText, review.rating);
      setDraftResponse(res.data.draft);
      toast.success('Draft generated');
    } catch (error) {
      toast.error('Failed to generate draft');
    } finally {
      setDraftLoading(false);
    }
  };

  const handleSaveResponse = async (approve = false) => {
    if (!selectedReview || !draftResponse) return;
    try {
      await updateReviewResponse(selectedReview.id, draftResponse, approve);
      setSelectedReview(null);
      setDraftResponse('');
      await loadData();
      toast.success(approve ? 'Response approved and saved' : 'Draft saved');
    } catch (error) {
      toast.error('Failed to save response');
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map(i => (
          <Star 
            key={i} 
            className={`h-4 w-4 ${i <= rating ? 'fill-amber-400 text-amber-400' : 'text-stone-300'}`} 
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <DeskLayout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Skeleton className="lg:col-span-2 h-96" />
            <Skeleton className="h-96" />
          </div>
        </div>
      </DeskLayout>
    );
  }

  return (
    <DeskLayout>
      <div className="space-y-6 animate-fade-in" data-testid="reviews-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-medium text-stone-900">Reviews</h1>
            <p className="text-stone-500 mt-1">Manage and respond to guest reviews</p>
          </div>
          <Dialog open={showNewReview} onOpenChange={setShowNewReview}>
            <DialogTrigger asChild>
              <Button className="bg-[#3F617E] hover:bg-[#345470] rounded-full" data-testid="add-review-button">
                <Plus className="h-4 w-4 mr-1" /> Add Review
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading">Add New Review</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Property *</Label>
                  <Select value={newReviewData.propertyId} onValueChange={(v) => setNewReviewData({...newReviewData, propertyId: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select property" />
                    </SelectTrigger>
                    <SelectContent>
                      {properties.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Platform</Label>
                    <Select value={newReviewData.platform} onValueChange={(v) => setNewReviewData({...newReviewData, platform: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="AIRBNB">Airbnb</SelectItem>
                        <SelectItem value="BOOKING">Booking.com</SelectItem>
                        <SelectItem value="GOOGLE">Google</SelectItem>
                        <SelectItem value="TRIPADVISOR">TripAdvisor</SelectItem>
                        <SelectItem value="OTHER">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Rating</Label>
                    <Select value={String(newReviewData.rating)} onValueChange={(v) => setNewReviewData({...newReviewData, rating: parseInt(v)})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[5, 4, 3, 2, 1].map(r => (
                          <SelectItem key={r} value={String(r)}>{r} stars</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Reviewer Name</Label>
                  <Input 
                    value={newReviewData.reviewerName} 
                    onChange={(e) => setNewReviewData({...newReviewData, reviewerName: e.target.value})}
                    placeholder="Guest name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Review Text *</Label>
                  <Textarea 
                    value={newReviewData.reviewText} 
                    onChange={(e) => setNewReviewData({...newReviewData, reviewText: e.target.value})}
                    placeholder="Paste the review text here..."
                    rows={4}
                  />
                </div>
                <Button onClick={handleCreateReview} className="w-full bg-[#3F617E] hover:bg-[#345470] rounded-full">
                  Add Review
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <div className="flex gap-4">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-48" data-testid="filter-status-select">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="NEEDS_REPLY">Needs Reply</SelectItem>
              <SelectItem value="REPLIED">Replied</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Reviews Table */}
          <Card className="lg:col-span-2 border-stone-200">
            <CardHeader className="pb-4">
              <CardTitle className="font-heading text-xl">All Reviews</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Review</TableHead>
                    <TableHead>Platform</TableHead>
                    <TableHead>Rating</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredReviews.map((review) => (
                    <TableRow key={review.id} data-testid={`review-row-${review.id}`}>
                      <TableCell className="max-w-xs">
                        <p className="font-medium truncate">{review.reviewerName || 'Anonymous'}</p>
                        <p className="text-sm text-stone-500 truncate">{review.reviewText.slice(0, 50)}...</p>
                        <p className="text-xs text-stone-400">{getPropertyName(review.propertyId)}</p>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{review.platform}</Badge>
                      </TableCell>
                      <TableCell>{renderStars(review.rating)}</TableCell>
                      <TableCell>
                        <Badge className={review.status === 'NEEDS_REPLY' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}>
                          {review.status === 'NEEDS_REPLY' ? 'Needs Reply' : 'Replied'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="rounded-full"
                          onClick={() => handleGenerateDraft(review)}
                          disabled={draftLoading && selectedReview?.id === review.id}
                          data-testid={`draft-response-${review.id}`}
                        >
                          {draftLoading && selectedReview?.id === review.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <><PenLine className="h-4 w-4 mr-1" /> Draft</>
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Insights Panel */}
          <div className="space-y-6">
            {/* Draft Response Card */}
            {selectedReview && (
              <Card className="border-stone-200">
                <CardHeader className="pb-4">
                  <CardTitle className="font-heading text-lg">Draft Response</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-3 bg-stone-50 rounded-lg">
                    <p className="text-sm text-stone-600">{selectedReview.reviewText}</p>
                    <p className="text-xs text-stone-400 mt-2">— {selectedReview.reviewerName}</p>
                  </div>
                  <Textarea
                    value={draftResponse}
                    onChange={(e) => setDraftResponse(e.target.value)}
                    placeholder="Generated response will appear here..."
                    rows={6}
                    data-testid="draft-response-textarea"
                  />
                  <div className="flex gap-2">
                    <Button 
                      onClick={() => handleSaveResponse(false)}
                      variant="outline"
                      className="flex-1 rounded-full"
                    >
                      Save Draft
                    </Button>
                    <Button 
                      onClick={() => handleSaveResponse(true)}
                      className="flex-1 bg-[#3F617E] hover:bg-[#345470] rounded-full"
                      data-testid="approve-response-button"
                    >
                      <Check className="h-4 w-4 mr-1" /> Approve
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Insights Card */}
            <Card className="border-stone-200">
              <CardHeader className="pb-4">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Lightbulb className="h-5 w-5 text-amber-500" />
                  Monthly Insights
                </CardTitle>
              </CardHeader>
              <CardContent>
                {selectedPropertyId === 'all' ? (
                  <p className="text-sm text-stone-500 text-center py-4">
                    Select a property to see insights
                  </p>
                ) : insightsLoading ? (
                  <div className="space-y-3">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                  </div>
                ) : insights ? (
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm font-medium flex items-center gap-2 text-green-700 mb-2">
                        <TrendingUp className="h-4 w-4" /> Top Praise
                      </p>
                      <ul className="space-y-1">
                        {insights.praiseThemes?.map((theme, i) => (
                          <li key={i} className="text-sm text-stone-600">• {theme}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <p className="text-sm font-medium flex items-center gap-2 text-red-700 mb-2">
                        <TrendingDown className="h-4 w-4" /> Top Complaints
                      </p>
                      <ul className="space-y-1">
                        {insights.complaintThemes?.map((theme, i) => (
                          <li key={i} className="text-sm text-stone-600">• {theme}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <p className="text-sm font-medium flex items-center gap-2 text-blue-700 mb-2">
                        <Lightbulb className="h-4 w-4" /> Recommendations
                      </p>
                      <ul className="space-y-1">
                        {insights.recommendations?.map((rec, i) => (
                          <li key={i} className="text-sm text-stone-600">• {rec}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-stone-500 text-center py-4">
                    No insights available
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DeskLayout>
  );
};

export default Reviews;
